import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMapEvents, useMap } from 'react-leaflet';
import type { Listing } from '../../types/index.js';
import type { BebauungsplanListItem } from '../../types/bebauungsplan';
import { GEBIETSART_LABELS } from '../../types/bebauungsplan';
import PlatformBadges from '../listings/PlatformBadges';
import L from 'leaflet';
import { appColors } from '../../config/appTheme';

// SVG-based colored marker icons
function createPinIcon(width: number, height: number, fill: string, stroke: string, center: string, ariaLabel?: string) {
  return L.divIcon({
    className: '',
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height],
    html: `<div class="marker-pin"${ariaLabel ? ` aria-label="${ariaLabel}"` : ''}><svg width="${width}" height="${height}" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>
      ${center}
    </svg></div>`,
  });
}

function createMarkerIcon(fill: string, stroke: string, size: number) {
  return createPinIcon(
    size,
    Math.round(41 * size / 25),
    fill,
    stroke,
    `<circle cx="12.5" cy="12.5" r="5" fill="${appColors.white}" opacity="0.8"/>`,
  );
}

function createListingIconSet(color: { 300: string; 600: string }, selectedStroke: string) {
  return {
    normal: createMarkerIcon(color[300], color[600], 25),
    hover: createMarkerIcon(color[300], color[600], 33),
    // Selected: darker/more saturated color, slightly larger
    selected: createMarkerIcon(color[600], selectedStroke, 30),
    selectedHover: createMarkerIcon(color[600], selectedStroke, 36),
  };
}

const listingIcons = {
  verified: createListingIconSet(appColors.success, appColors.success[900]),
  unverified: createListingIconSet(appColors.orange, appColors.orange[800]),
};

function getListingIcon(verified: boolean, selected: boolean, hovered: boolean) {
  const icons = verified ? listingIcons.verified : listingIcons.unverified;
  if (selected) return hovered ? icons.selectedHover : icons.selected;
  return hovered ? icons.hover : icons.normal;
}

L.Marker.prototype.options.icon = listingIcons.unverified.normal;

// Generic marker for non-listing entities
export interface MapMarker {
  id: number;
  latitude: number;
  longitude: number;
  label: string;
  sublabel?: string;
  color: 'green' | 'blue' | 'purple' | 'indigo' | 'teal' | 'orange';
  link: string;
  linkLabel?: string;
}

// Normal and selected (darker color, slightly larger) icon colors per generic marker color
const markerColors: Record<MapMarker['color'], { fill: string; stroke: string; selectedFill: string; selectedStroke: string }> = {
  green:  { fill: appColors.success[300], stroke: appColors.success[600], selectedFill: appColors.success[600], selectedStroke: appColors.success[900] },
  blue:   { fill: appColors.primary[300], stroke: appColors.primary[600], selectedFill: appColors.primary[600], selectedStroke: appColors.primary[900] },
  purple: { fill: appColors.violet[300], stroke: appColors.violet[600], selectedFill: appColors.violet[600], selectedStroke: appColors.violet[900] },
  indigo: { fill: appColors.indigo[300], stroke: appColors.indigo[700], selectedFill: appColors.indigo[700], selectedStroke: appColors.indigo[950] },
  teal:   { fill: appColors.teal[300], stroke: appColors.teal[600], selectedFill: appColors.teal[600], selectedStroke: appColors.teal[900] },
  orange: { fill: appColors.orange[300], stroke: appColors.orange[600], selectedFill: appColors.orange[600], selectedStroke: appColors.orange[800] },
};

// Pre-create icons for each color
const genericIcons = Object.fromEntries(
  Object.entries(markerColors).map(([key, colors]) => [key, {
    normal: createMarkerIcon(colors.fill, colors.stroke, 25),
    selected: createMarkerIcon(colors.selectedFill, colors.selectedStroke, 30),
  }])
) as Record<MapMarker['color'], { normal: L.DivIcon; selected: L.DivIcon }>;

function createListingClusterIcon(count: number) {
  const fontSize = count > 99 ? 8 : count > 9 ? 9.5 : 11;
  return createPinIcon(
    32,
    52,
    appColors.orange[300],
    appColors.orange[600],
    `<text x="12.5" y="16" text-anchor="middle" font-family="Arial,sans-serif" font-size="${fontSize}" font-weight="700" fill="white" stroke="${appColors.orange[800]}" stroke-width="0.6" paint-order="stroke">${count}</text>`,
    `${count} Inserate`,
  );
}

const listingMarkerKey = (listingId: number) => `listing-${listingId}`;
const entityMarkerKey = (marker: MapMarker) => `marker-${marker.color}-${marker.id}`;

function hasValidCoordinates(point: { latitude?: number | null; longitude?: number | null }) {
  return point.latitude != null && point.longitude != null && point.latitude !== 0 && point.longitude !== 0;
}

interface ListingGroup {
  key: string;
  listings: Listing[];
  position: [number, number];
}

function groupListingsByScreenPosition(listings: Listing[], zoom: number): ListingGroup[] {
  const clusterRadius = 40;
  const buckets = new globalThis.Map<string, number[]>();
  const clusters: Array<{
    listings: Listing[];
    x: number;
    y: number;
    bucketKey: string;
  }> = [];

  for (const listing of listings) {
    const point = L.CRS.EPSG3857.latLngToPoint(
      L.latLng(listing.latitude!, listing.longitude!),
      zoom,
    );
    const bucketX = Math.floor(point.x / clusterRadius);
    const bucketY = Math.floor(point.y / clusterRadius);
    let closestIndex = -1;
    let closestDistance = clusterRadius;

    for (let x = bucketX - 1; x <= bucketX + 1; x += 1) {
      for (let y = bucketY - 1; y <= bucketY + 1; y += 1) {
        for (const index of buckets.get(`${x},${y}`) || []) {
          const cluster = clusters[index];
          const distance = Math.hypot(point.x - cluster.x, point.y - cluster.y);
          if (distance <= closestDistance) {
            closestIndex = index;
            closestDistance = distance;
          }
        }
      }
    }

    if (closestIndex === -1) {
      const bucketKey = `${bucketX},${bucketY}`;
      const index = clusters.length;
      clusters.push({ listings: [listing], x: point.x, y: point.y, bucketKey });
      buckets.set(bucketKey, [...(buckets.get(bucketKey) || []), index]);
      continue;
    }

    const cluster = clusters[closestIndex];
    const previousBucketKey = cluster.bucketKey;
    const count = cluster.listings.length;
    cluster.listings.push(listing);
    cluster.x = (cluster.x * count + point.x) / (count + 1);
    cluster.y = (cluster.y * count + point.y) / (count + 1);

    const nextBucketKey = `${Math.floor(cluster.x / clusterRadius)},${Math.floor(cluster.y / clusterRadius)}`;
    if (nextBucketKey !== previousBucketKey) {
      const previousBucket = buckets.get(previousBucketKey) || [];
      buckets.set(previousBucketKey, previousBucket.filter(index => index !== closestIndex));
      buckets.set(nextBucketKey, [...(buckets.get(nextBucketKey) || []), closestIndex]);
      cluster.bucketKey = nextBucketKey;
    }
  }

  return clusters.map((cluster) => {
    const position = L.CRS.EPSG3857.pointToLatLng(
      L.point(cluster.x, cluster.y),
      zoom,
    );
    return {
      key: cluster.listings.length === 1
        ? listingMarkerKey(cluster.listings[0].id)
        : `cluster-${cluster.listings.map(listing => listing.id).sort((a, b) => a - b).join('-')}`,
      listings: cluster.listings,
      position: [position.lat, position.lng],
    };
  });
}

function findGroupOfListing(groups: ListingGroup[], listingId: number) {
  return groups.find(group => group.listings.some(listing => listing.id === listingId));
}

interface MapProps {
  listings?: Listing[];
  markers?: MapMarker[];
  /** Bebauungsplan-Polygone für den optionalen B-Plan-Layer. Geometrie muss befüllt sein. */
  bebauungsplaene?: BebauungsplanListItem[];
  center?: [number, number];
  zoom?: number;
  hoveredListingId?: number | null;
  onHoverListing?: (id: number | null) => void;
  onMapMove?: (center: [number, number], zoom: number) => void;
  /** When set, auto-fit bounds to markers. Value change triggers re-fit. */
  fitBoundsKey?: string;
  /** When set, pre-selects this listing marker and flies to it. Skips fitBounds. */
  selectedListingId?: number | null;
  /** When set, pre-selects this generic marker (by id) and flies to it. Skips fitBounds. */
  focusedMarkerId?: number | null;
  /** When true, drag (with or without shift) draws a selection rectangle. */
  selectionMode?: boolean;
  /** IDs of listings currently in the bulk selection — drawn with a highlight ring. */
  selectedListingIds?: Set<number>;
  /** Called when the user finishes drawing a selection rectangle. */
  onSelectInBounds?: (bounds: L.LatLngBounds, additive: boolean) => void;
  /** Toggles the persistent selection mode. When provided, a toggle button is rendered. */
  onToggleSelectionMode?: () => void;
}

function ListingMapPopupCard({ listing, onFocus }: {
  listing: Listing;
  onFocus?: () => void;
}) {
  return (
    <div className="w-[280px] max-w-[calc(100vw-64px)]">
      <div className={`h-1 ${listing.address_verified ? 'bg-green-500' : 'bg-amber-400'}`} />
      <div className="p-4">
        {/* Without the cluster pager above, the popup close button sits over this row. */}
        <div className={`flex items-center justify-between gap-2 mb-3 ${onFocus ? '' : 'pr-6'}`}>
          <PlatformBadges platforms={listing.platforms} fallback={listing.platform} />
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium leading-4 ring-1 ${
            listing.address_verified
              ? 'bg-green-50 text-green-700 ring-green-200'
              : 'bg-amber-50 text-amber-700 ring-amber-200'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${listing.address_verified ? 'bg-green-500' : 'bg-amber-400'}`} />
            {listing.address_verified ? 'Verifiziert' : 'Nicht verifiziert'}
          </span>
        </div>

        <h3 className="font-semibold text-sm text-gray-900 leading-snug line-clamp-2 mb-2 pr-4">
          {listing.title}
        </h3>

        {(listing.address || listing.zipcode || listing.city) && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500 mb-3">
            <svg className="w-3.5 h-3.5 mt-px shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span>{[listing.address, [listing.zipcode, listing.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span>
          </div>
        )}

        {(listing.listing_type || listing.size || listing.price_per_night) && (
          <div className="flex items-center flex-wrap gap-1.5 mb-4">
            {listing.listing_type && (
              <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{listing.listing_type}</span>
            )}
            {listing.size && (
              <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{listing.size}</span>
            )}
            {listing.price_per_night && (
              <span className="text-[11px] bg-gray-100 text-gray-700 font-semibold px-2 py-0.5 rounded-md">
                {listing.price_per_night} {listing.currency}/Nacht
              </span>
            )}
          </div>
        )}

        <div className="border-t border-gray-100 mb-3" />
        <div className="flex flex-col gap-2">
          {onFocus && (
            <button
              onClick={onFocus}
              className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-800 transition-colors hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              Auf Karte fokussieren
            </button>
          )}
          <button
            onClick={() => window.open(`/inserat/${listing.id}`, '_blank')}
            className="btn btn-primary w-full text-xs font-semibold px-3"
          >
            Details ansehen
          </button>
          <button
            onClick={() => window.open(listing.resolved_url || listing.listing_url, '_blank')}
            className="btn btn-outline w-full text-xs px-3 flex items-center justify-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Auf {listing.platform === 'unknown' ? 'HomeToGo' : listing.platform} ansehen
          </button>
        </div>
      </div>
    </div>
  );
}

const listingPopupProps = { className: 'modern-popup listing-popup', maxWidth: 320, autoPan: false } as const;

function PagerButton({ label, disabled, onClick, children }: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function ListingClusterPopup({ listings, initialListingId, onFocus }: {
  listings: Listing[];
  initialListingId?: number | null;
  onFocus: (listing: Listing) => void;
}) {
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, listings.findIndex(l => l.id === initialListingId)));
  const listing = listings[activeIndex];

  return (
    <div>
      <div className="flex items-center gap-1 border-b border-gray-100 bg-gray-50 py-2 pl-3 pr-10">
        <PagerButton
          label="Vorheriges Inserat"
          disabled={activeIndex === 0}
          onClick={() => setActiveIndex(index => index - 1)}
        >
          ‹
        </PagerButton>
        <span className="min-w-[3rem] text-center text-xs font-medium tabular-nums text-gray-500">
          {activeIndex + 1} / {listings.length}
        </span>
        <PagerButton
          label="Nächstes Inserat"
          disabled={activeIndex === listings.length - 1}
          onClick={() => setActiveIndex(index => index + 1)}
        >
          ›
        </PagerButton>
      </div>
      <ListingMapPopupCard listing={listing} onFocus={() => onFocus(listing)} />
    </div>
  );
}

function MapEventHandler({ onMapMove, onMapClick }: {
  onMapMove?: (center: [number, number], zoom: number) => void;
  onMapClick?: () => void;
}) {
  useMapEvents({
    moveend: (e) => {
      const center = e.target.getCenter();
      const zoom = e.target.getZoom();
      onMapMove?.([center.lat, center.lng], zoom);
    },
    click: () => onMapClick?.(),
  });
  return null;
}

function MapZoomController({ onZoomChange, onMapReady }: {
  onZoomChange: (zoom: number) => void;
  onMapReady: (map: L.Map) => void;
}) {
  const map = useMap();
  useEffect(() => {
    onMapReady(map);
    onZoomChange(map.getZoom());
  }, [map, onMapReady, onZoomChange]);
  useMapEvents({
    zoomend: () => onZoomChange(map.getZoom()),
  });
  return null;
}

/** Pans the map on open so a listing popup's pin sits low and centered, with the whole popup in view. */
function ListingPopupPositionController() {
  const map = useMap();

  useEffect(() => {
    const handlePopupOpen = (event: L.PopupEvent) => {
      if (!event.popup.options.className?.includes('listing-popup')) return;

      window.requestAnimationFrame(() => {
        if (!map.hasLayer(event.popup)) return;
        const popupPosition = event.popup.getLatLng();
        const popupElement = event.popup.getElement();
        if (!popupPosition || !popupElement) return;
        const pin = map.latLngToContainerPoint(popupPosition);
        const size = map.getSize();
        const mapRect = map.getContainer().getBoundingClientRect();
        const popupRect = popupElement.getBoundingClientRect();
        const leftOffset = pin.x - (popupRect.left - mapRect.left);
        const rightOffset = popupRect.right - mapRect.left - pin.x;
        const topOffset = pin.y - (popupRect.top - mapRect.top);
        const bottomOffset = popupRect.bottom - mapRect.top - pin.y;
        const minX = leftOffset + 16;
        const maxX = size.x - rightOffset - 16;
        const minY = topOffset + 16;
        const maxY = size.y - bottomOffset - 16;
        const targetX = Math.max(minX, Math.min(maxX, size.x / 2));
        const targetY = Math.max(minY, Math.min(maxY, size.y * 0.68));
        const target = L.point(targetX, targetY);
        map.panBy(pin.subtract(target), { animate: true, duration: 0.25 });
      });
    };

    map.on('popupopen', handlePopupOpen);
    return () => {
      map.off('popupopen', handlePopupOpen);
    };
  }, [map]);

  return null;
}

/** Automatically fits map bounds to a set of points when fitBoundsKey changes */
function FitBoundsController({ points, fitBoundsKey, fallbackCenter }: {
  points: [number, number][];
  fitBoundsKey?: string;
  fallbackCenter?: [number, number];
}) {
  const map = useMap();
  const lastKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (fitBoundsKey === undefined || fitBoundsKey === lastKeyRef.current) return;
    lastKeyRef.current = fitBoundsKey;

    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } else if (fallbackCenter) {
      map.setView(fallbackCenter, 12);
    }
  }, [fitBoundsKey, points, fallbackCenter, map]);

  return null;
}

/**
 * Lets the user draw a rectangle on the map to lasso-select markers.
 * Active when `selectionMode` is true (any drag) OR when shift is held (any time).
 * Disables Leaflet's built-in boxZoom so shift+drag does not zoom.
 */
function BoxSelectController({ selectionMode, onSelect }: {
  selectionMode: boolean;
  onSelect?: (bounds: L.LatLngBounds, additive: boolean) => void;
}) {
  const map = useMap();
  const startRef = useRef<L.LatLng | null>(null);
  const rectRef = useRef<L.Rectangle | null>(null);
  const additiveRef = useRef(false);
  const draggingRef = useRef(false);

  useEffect(() => {
    // boxZoom is the shift+drag zoom; we always disable it because shift+drag is now selection
    map.boxZoom.disable();
    return () => {
      map.boxZoom.enable();
    };
  }, [map]);

  useEffect(() => {
    const container = map.getContainer();

    const cleanupRect = () => {
      if (rectRef.current) {
        rectRef.current.remove();
        rectRef.current = null;
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      // Only react to plain left-button drags; modifiers other than shift/ctrl/meta we ignore
      if (e.button !== 0) return;
      const useSelection = selectionMode || e.shiftKey;
      if (!useSelection) return;

      e.preventDefault();
      map.dragging.disable();
      draggingRef.current = true;
      additiveRef.current = e.ctrlKey || e.metaKey;
      startRef.current = map.mouseEventToLatLng(e);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current || !startRef.current) return;
      const current = map.mouseEventToLatLng(e);
      const bounds = L.latLngBounds(startRef.current, current);
      if (!rectRef.current) {
        rectRef.current = L.rectangle(bounds, {
          className: 'map-lasso-rect',
          interactive: false,
        }).addTo(map);
      } else {
        rectRef.current.setBounds(bounds);
      }
    };

    const onMouseUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      map.dragging.enable();
      const start = startRef.current;
      startRef.current = null;
      const finalBounds = rectRef.current?.getBounds() ?? null;
      cleanupRect();
      // Ignore accidental tiny drags (single-pixel jitter when clicking)
      if (start && finalBounds) {
        const sw = map.latLngToContainerPoint(finalBounds.getSouthWest());
        const ne = map.latLngToContainerPoint(finalBounds.getNorthEast());
        if (Math.abs(sw.x - ne.x) > 6 && Math.abs(sw.y - ne.y) > 6) {
          onSelect?.(finalBounds, additiveRef.current);
        }
      }
    };

    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      cleanupRect();
      map.dragging.enable();
    };
  }, [map, selectionMode, onSelect]);

  return null;
}

/** Flies to a position once — used to center on a pre-selected marker */
function FlyToController({ position }: { position: [number, number] | null }) {
  const map = useMap();
  const didFly = useRef(false);

  useEffect(() => {
    if (!position || didFly.current) return;
    didFly.current = true;
    map.setView(position, 16);
  }, [position, map]);

  return null;
}

function getPinElement(marker: L.Marker | null | undefined) {
  return marker?.getElement()?.querySelector<HTMLElement>('.marker-pin') ?? null;
}

function restartPinAnimation(marker: L.Marker | null | undefined, animationClass: string) {
  const pin = getPinElement(marker);
  if (!pin) return;
  pin.classList.remove('marker-pin-anim', 'marker-pin-desel');
  void pin.offsetHeight; // force reflow so the animation replays
  pin.classList.add(animationClass);
}

const mapToggleButtonClass = (active: boolean) =>
  `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
    active
      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
      : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
  }`;

const Map: React.FC<MapProps> = ({ listings, markers, bebauungsplaene, center, zoom = 11, hoveredListingId, onHoverListing, onMapMove, fitBoundsKey, selectedListingId, focusedMarkerId, selectionMode, selectedListingIds, onSelectInBounds, onToggleSelectionMode }) => {
  const [satellite, setSatellite] = useState(false);
  const [showBebauungsplaene, setShowBebauungsplaene] = useState(false);
  const [mapZoom, setMapZoom] = useState(zoom);
  const mapRef = useRef<L.Map | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(() => {
    if (selectedListingId) return listingMarkerKey(selectedListingId);
    if (focusedMarkerId != null) {
      const m = (markers || []).find(m => m.id === focusedMarkerId);
      if (m) return entityMarkerKey(m);
    }
    return null;
  });
  // deselectingKey holds the marker currently playing the scale-out animation.
  // It still renders with the selected (larger) icon so the animation has something to shrink.
  const [deselectingKey, setDeselectingKey] = useState<string | null>(null);
  const markerRefs = useRef<Record<string, L.Marker | null>>({});
  const deselectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleZoomChange = useCallback((nextZoom: number) => {
    setMapZoom(current => current === nextZoom ? current : nextZoom);
  }, []);
  const handleMapReady = useCallback((map: L.Map) => {
    mapRef.current = map;
  }, []);
  // Marker key whose popup should open once the "Auf Karte fokussieren" flight has re-rendered the markers.
  const pendingPopupKeyRef = useRef<string | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const [clusterStartListingId, setClusterStartListingId] = useState<number | null>(null);

  const cancelDeselectAnimation = useCallback(() => {
    if (!deselectTimerRef.current) return;
    clearTimeout(deselectTimerRef.current);
    deselectTimerRef.current = null;
    setDeselectingKey(null);
  }, []);

  // Closes the popup and keeps the selected icon visible while the scale-out animation plays.
  const startDeselectAnimation = useCallback((key: string) => {
    markerRefs.current[key]?.closePopup();
    setDeselectingKey(key);
    deselectTimerRef.current = setTimeout(() => {
      setDeselectingKey(null);
      deselectTimerRef.current = null;
    }, 180);
  }, []);

  const handleSelect = useCallback((key: string) => {
    cancelDeselectAnimation();
    if (selectedKey === key) {
      startDeselectAnimation(key);
      setSelectedKey(null);
    } else {
      if (selectedKey) startDeselectAnimation(selectedKey);
      setSelectedKey(key);
    }
  }, [selectedKey, cancelDeselectAnimation, startDeselectAnimation]);

  const handleDeselect = useCallback(() => {
    if (!selectedKey) return;
    cancelDeselectAnimation();
    startDeselectAnimation(selectedKey);
    setSelectedKey(null);
  }, [selectedKey, cancelDeselectAnimation, startDeselectAnimation]);

  // Pop-in animation for newly selected marker
  useEffect(() => {
    if (selectedKey) restartPinAnimation(markerRefs.current[selectedKey], 'marker-pin-anim');
  }, [selectedKey]);

  // Pop-out animation for deselecting marker (icon stays selected while this plays)
  useEffect(() => {
    if (deselectingKey) restartPinAnimation(markerRefs.current[deselectingKey], 'marker-pin-desel');
  }, [deselectingKey]);

  const validListings = useMemo(() => (listings || []).filter(hasValidCoordinates), [listings]);

  const listingGroups = useMemo(
    () => groupListingsByScreenPosition(validListings, mapZoom),
    [validListings, mapZoom],
  );

  const handleFocusListing = (clusterKey: string, listing: Listing) => {
    markerRefs.current[clusterKey]?.closePopup();
    const map = mapRef.current;
    if (!map) return;
    const nextZoom = Math.min(Math.max(map.getZoom() + 2, 16), 18);
    map.once('moveend', () => {
      const group = findGroupOfListing(groupListingsByScreenPosition(validListings, map.getZoom()), listing.id);
      if (!group) return;
      if (group.listings.length > 1) setClusterStartListingId(listing.id);
      else handleSelect(group.key);
      pendingPopupKeyRef.current = group.key;
      setFocusRequest(request => request + 1);
    });
    map.flyTo([listing.latitude!, listing.longitude!], nextZoom, { duration: 0.5 });
  };

  // Runs after the markers were regrouped for the new zoom, so the target marker exists.
  // Deferred so a freshly mounted marker is fully attached to the map before its popup opens.
  useEffect(() => {
    const key = pendingPopupKeyRef.current;
    if (!key) return;
    const id = window.setTimeout(() => {
      const marker = markerRefs.current[key];
      if (!marker) return;
      pendingPopupKeyRef.current = null;
      marker.openPopup();
    }, 0);
    return () => window.clearTimeout(id);
  }, [focusRequest, listingGroups]);

  const validMarkers = useMemo(() => (markers || []).filter(hasValidCoordinates), [markers]);

  // Multi-selection highlight ring: toggle a CSS class on each affected marker's pin element.
  // Runs after the listing markers render so marker DOM elements exist.
  useEffect(() => {
    Object.entries(markerRefs.current).forEach(([key, marker]) => {
      if (!key.startsWith('listing-')) return;
      const pin = getPinElement(marker);
      if (!pin) return;
      const id = Number(key.slice('listing-'.length));
      if (selectedListingIds?.has(id)) pin.classList.add('marker-pin-selected');
      else pin.classList.remove('marker-pin-selected');
    });
  }, [selectedListingIds, listingGroups]);

  // All points for bounds calculation
  const allPoints: [number, number][] = useMemo(() => [
    ...validListings.map(l => [l.latitude!, l.longitude!] as [number, number]),
    ...validMarkers.map(m => [m.latitude, m.longitude] as [number, number]),
  ], [validListings, validMarkers]);

  const mapCenter: [number, number] = center || (
    allPoints.length > 0
      ? [
          allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length,
          allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length,
        ]
      : [52.5200, 13.4050] // Default to Berlin
  );

  const flyToPosition = useMemo<[number, number] | null>(() => {
    if (selectedListingId) {
      const l = validListings.find(l => l.id === selectedListingId);
      return l ? [l.latitude!, l.longitude!] : null;
    }
    if (focusedMarkerId != null) {
      const m = validMarkers.find(m => m.id === focusedMarkerId);
      return m ? [m.latitude, m.longitude] : null;
    }
    return null;
  }, [selectedListingId, focusedMarkerId, validListings, validMarkers]);

  // Auto-open the popup of a pre-focused marker once on mount.
  // Subsequent clicks are handled by Leaflet's default popup behavior.
  const didAutoOpenRef = useRef(false);
  useEffect(() => {
    if (didAutoOpenRef.current) return;
    const initialKey =
      selectedListingId
        ? findGroupOfListing(listingGroups, selectedListingId)?.key
      : focusedMarkerId != null
        ? (() => {
            const m = validMarkers.find(m => m.id === focusedMarkerId);
            return m ? entityMarkerKey(m) : null;
          })()
        : null;
    if (!initialKey) return;
    const marker = markerRefs.current[initialKey];
    if (!marker) return;
    const id = window.setTimeout(() => {
      const currentMarker = markerRefs.current[initialKey];
      if (!currentMarker) return;
      currentMarker.openPopup();
      didAutoOpenRef.current = true;
    }, 300);
    return () => window.clearTimeout(id);
  }, [selectedListingId, focusedMarkerId, validMarkers, listingGroups]);

  return (
    <div className="h-full w-full relative">
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        {satellite ? (
          <TileLayer
            key="satellite"
            attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        ) : (
          <TileLayer
            key="osm"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        <MapEventHandler onMapMove={onMapMove} onMapClick={handleDeselect} />
        <MapZoomController onZoomChange={handleZoomChange} onMapReady={handleMapReady} />
        <ListingPopupPositionController />
        {onSelectInBounds && (
          <BoxSelectController selectionMode={!!selectionMode} onSelect={onSelectInBounds} />
        )}
        {(selectedListingId || focusedMarkerId != null)
          ? <FlyToController position={flyToPosition} />
          : <FitBoundsController points={allPoints} fitBoundsKey={fitBoundsKey} fallbackCenter={center} />
        }

        {/* Bebauungsplan-Polygone (Layer unter den Markern) */}
        {showBebauungsplaene && bebauungsplaene?.map(bp => {
          if (!bp.geometry_geojson) return null;
          const isWR = bp.gebietsart === 'WR';
          const style = isWR
            ? { color: appColors.danger[600], fillColor: appColors.danger[300], fillOpacity: 0.35, weight: 2 }
            : { color: appColors.primary[600], fillColor: appColors.primary[300], fillOpacity: 0.20, weight: 1 };
          return (
            <GeoJSON
              key={`bplan-${bp.id}`}
              data={bp.geometry_geojson}
              style={() => style}
            >
              <Popup className="modern-popup">
                <div className="w-[260px]">
                  <div className={`h-1 ${isWR ? 'bg-red-500' : 'bg-blue-500'}`} />
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 leading-none">
                        Bebauungsplan
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium leading-none ring-1 ${
                        isWR
                          ? 'bg-red-50 text-red-700 ring-red-200'
                          : 'bg-blue-50 text-blue-700 ring-blue-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isWR ? 'bg-red-500' : 'bg-blue-500'}`} />
                        {GEBIETSART_LABELS[bp.gebietsart] ?? bp.gebietsart}
                      </span>
                    </div>
                    <h3 className="font-semibold text-sm text-gray-900 leading-snug mb-1">
                      {bp.name}
                    </h3>
                    {bp.plan_nr && (
                      <div className="text-xs text-gray-500 mb-2">Plan-Nr.: {bp.plan_nr}</div>
                    )}
                    {bp.rechtskraft_datum && (
                      <div className="text-xs text-gray-400 mb-3">
                        Rechtskräftig seit {bp.rechtskraft_datum}
                      </div>
                    )}
                    <div className="border-t border-gray-100 mb-3" />
                    <button
                      onClick={() => window.open(`/bebauungsplaene/${bp.id}`, '_blank')}
                      className="btn btn-primary w-full text-xs font-semibold px-3"
                    >
                      Details ansehen
                    </button>
                  </div>
                </div>
              </Popup>
            </GeoJSON>
          );
        })}

        {/* Listing markers */}
        {listingGroups.map((group) => {
          if (group.listings.length > 1) {
            const clusterKey = group.key;
            return (
              <Marker
                key={clusterKey}
                ref={(ref) => { markerRefs.current[clusterKey] = ref; }}
                position={group.position}
                icon={createListingClusterIcon(group.listings.length)}
                zIndexOffset={1000}
                eventHandlers={{ click: () => setClusterStartListingId(null) }}
              >
                <Popup {...listingPopupProps}>
                  <ListingClusterPopup
                    key={clusterKey}
                    listings={group.listings}
                    initialListingId={clusterStartListingId}
                    onFocus={(listing) => handleFocusListing(clusterKey, listing)}
                  />
                </Popup>
              </Marker>
            );
          }

          const listing = group.listings[0];
          const lKey = group.key;
          // show selected icon while animating out (deselectingKey) so the shrink has something to shrink
          const isSelected = selectedKey === lKey || deselectingKey === lKey;
          const isHovered = listing.id === hoveredListingId;
          return (
          <Marker
            key={lKey}
            ref={(ref) => { markerRefs.current[lKey] = ref; }}
            position={[listing.latitude!, listing.longitude!]}
            icon={getListingIcon(!!listing.address_verified, isSelected, isHovered)}
            zIndexOffset={isSelected ? 2000 : isHovered ? 1000 : 0}
            eventHandlers={{
              mouseover: () => onHoverListing?.(listing.id),
              mouseout: () => onHoverListing?.(null),
              click: () => handleSelect(lKey),
            }}
          >
            <Popup {...listingPopupProps}>
              <ListingMapPopupCard listing={listing} />
            </Popup>
          </Marker>
          );
        })}

        {/* Generic entity markers */}
        {validMarkers.map((marker) => {
          const mKey = entityMarkerKey(marker);
          const isSelected = selectedKey === mKey || deselectingKey === mKey;
          return (
          <Marker
            key={mKey}
            ref={(ref) => { markerRefs.current[mKey] = ref; }}
            position={[marker.latitude, marker.longitude]}
            icon={isSelected ? genericIcons[marker.color].selected : genericIcons[marker.color].normal}
            zIndexOffset={isSelected ? 2000 : 0}
            eventHandlers={{ click: () => handleSelect(mKey) }}
          >
            <Popup className="modern-popup">
              <div className="min-w-[200px] max-w-[260px] p-3">
                <h3 className="font-semibold text-sm text-gray-900 leading-tight mb-1">{marker.label}</h3>
                {marker.sublabel && (
                  <p className="text-xs text-gray-500 mb-2">{marker.sublabel}</p>
                )}
                <button
                  onClick={() => window.open(marker.link, '_blank')}
                  className="btn btn-primary w-full text-xs px-3 py-1.5"
                >
                  {marker.linkLabel || 'Details ansehen'}
                </button>
              </div>
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>

      {/* Top-right control cluster: selection-mode toggle (+ help) and satellite toggle */}
      <div className="absolute top-3 right-3 z-[500] flex items-center gap-2">
        {onToggleSelectionMode && (
          <>
            <span
              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white text-gray-400 border border-gray-200 shadow-sm text-[11px] font-semibold cursor-help select-none"
              title="Mehrfachauswahl: Shift+Ziehen zeichnet ein Rechteck und markiert alle Inserate darin. Alternativ: Auswahl-Modus aktivieren und ohne Shift ziehen. Ctrl/Cmd+Ziehen erweitert eine bestehende Auswahl."
              aria-label="Hilfe zur Mehrfachauswahl"
            >
              ?
            </span>
            <button
              onClick={onToggleSelectionMode}
              title="Auswahl-Modus an/aus"
              aria-pressed={!!selectionMode}
              className={mapToggleButtonClass(!!selectionMode)}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2M20 8V6a2 2 0 00-2-2h-2M20 16v2a2 2 0 01-2 2h-2" />
              </svg>
              Auswahl
            </button>
          </>
        )}

        {/* Bebauungsplan layer toggle */}
        <button
          onClick={() => setShowBebauungsplaene(s => !s)}
          title="Bebauungspläne als Polygone einblenden"
          aria-pressed={showBebauungsplaene}
          className={mapToggleButtonClass(showBebauungsplaene)}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
          </svg>
          B-Pläne
        </button>

        {/* Satellite toggle button */}
        <button
          onClick={() => setSatellite(s => !s)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
            satellite
              ? 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
              : 'bg-gray-900 text-white border-gray-900 hover:bg-gray-700'
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
          </svg>
          {satellite ? 'Karte' : 'Satellit'}
        </button>
      </div>
    </div>
  );
};

export default Map;
